import { ShipperProfileDTO } from 'shared/modules/shipper/ShipperProfileDTO';
import { useEffect, useState } from 'react';
import {
  AppDrawer,
  ColorDynamic,
  Inline,
  Stack,
  useSnackbarStack,
} from '@superdispatch/ui';
import { ShipperReportReasonSelector } from './ShipperReportReasonSelector';
import { useCarrierSettings } from 'shared/settings/CarrierSettingsAPI';
import { useReportedShippers } from '../ReportedShippers';
import { useShipperAPI } from 'shared/modules/shipper/LoadboardShipperAPI';
import { useInitialValues } from 'shared/form/AppFormik';
import {
  ExtendedReason,
  isRatingReportReason,
  shipperReportSchema,
} from 'shared/modules/shipper/ShipperReportDTO';
import { FormikTextField, useFormikEnhanced } from '@superdispatch/forms';
import { trackEvent } from 'shared/helpers/Analytics';
import { logInfo } from 'shared/helpers/Logger';
import { FormikProvider } from 'formik';
import { Typography } from '@material-ui/core';
import {
  ShipperReportActions,
  ShipperReportContent,
} from './ShipperReportLayout';
import { Button, TextBox } from '@superdispatch/ui-lab';
import {
  RatingShipperForm,
  useRatingShipperForm,
} from 'shared/modules/ratings/RatingShipperForm';
import styled from 'styled-components';
import { ThumbDown } from '@material-ui/icons';
import { ShipperReportingConfirmation } from './ShipperReportingConfirmation';
import { startCase } from 'es-toolkit/compat';
import { isEmpty } from 'shared/helpers/CommonHelpers';
import { RatingRestricted } from './RatingRestricted';
import { useIsRatingAvailable } from 'shared/modules/ratings/ShipperRatingUtils';

const ThumbDownIcon = styled(ThumbDown)`
  font-size: 20px;
`;

interface ShipperRatingFormProps {
  shipper: ShipperProfileDTO;
  reason: ExtendedReason;
  onSubmitSuccess: () => void;
}

export function ShipperRatingFormRestricted(props: ShipperRatingFormProps) {
  const isRatingAvailable = useIsRatingAvailable(
    props.shipper.last_order_completed_date,
  );

  return isRatingAvailable ? (
    <ShipperRatingForm {...props} />
  ) : (
    <ShipperReportContent>
      <RatingRestricted />
    </ShipperReportContent>
  );
}

export function ShipperRatingForm({
  shipper,
  reason,
  onSubmitSuccess,
}: ShipperRatingFormProps) {
  const { formik } = useRatingShipperForm({
    guid: shipper.guid,
    ratingType: reason === 'RATING' ? undefined : 'NEGATIVE',
    medium: `Rating Form for ${startCase(reason)} reason on Carrier Report Drawer`,
    onSuccess: onSubmitSuccess,
  });

  return (
    <FormikProvider value={formik}>
      <ShipperReportContent>
        <RatingShipperForm
          hideRatingSelection={reason !== 'RATING'}
          additionalDetails={
            reason !== 'RATING' && (
              <Stack>
                <Inline verticalAlign="center">
                  <Typography variant="h4">Leave a negative rating</Typography>
                  <ThumbDownIcon htmlColor={ColorDynamic.Dark300} />
                </Inline>
                {reason === 'NON_PAYMENT' && (
                  <Typography>
                    Super Dispatch does not handle payment disputes directly.
                    They are between shippers and carriers. We suggest you leave
                    a rating.
                  </Typography>
                )}
              </Stack>
            )
          }
        />
      </ShipperReportContent>
      <ShipperReportActions>
        <Button
          pending={formik.isSubmitting}
          onClick={() => {
            formik.handleSubmit();
          }}
        >
          Submit
        </Button>
      </ShipperReportActions>
    </FormikProvider>
  );
}

const REPORT_REASONS_LABELS: Record<ExtendedReason, string> = {
  DOUBLE_BROKERING: 'Double-Brokering',
  IDENTITY_THEFT: 'Identity Theft',
  NON_PAYMENT: 'Non-payment',
  UNAUTHORIZED_DISPATCHING: 'Unauthorized Dispatching',
  SUSPICIOUS_ACTIVITY: 'Suspicious Activity',
  OTHER: 'Other',
  RATING: 'Rating',
} as const;

interface ShipperReportingFormProps {
  shipper: ShipperProfileDTO;
  reason: ExtendedReason;
  onSubmitSuccess: () => void;
}

export function ShipperReportingForm({
  shipper,
  reason,
  onSubmitSuccess,
}: ShipperReportingFormProps) {
  const { addSnackbar } = useSnackbarStack();
  const { data: carrierSettings } = useCarrierSettings();
  const { addReportedShipperGUID } = useReportedShippers();
  const { reportShipper } = useShipperAPI();

  const initialValues = useInitialValues(shipperReportSchema, {
    shipper_name: shipper.name,
    reason,
  });
  const formik = useFormikEnhanced({
    initialValues,
    validationSchema: shipperReportSchema,
    onSubmit(values) {
      return reportShipper(shipper.guid, values);
    },
    onSubmitSuccess(_response, values) {
      const details = {
        fraud_type: REPORT_REASONS_LABELS[values.reason],
        contact: values.contact,
        additional_details: values.description,
        user_guid: carrierSettings?.user.guid,
        shipper_guid: shipper.guid,
        shipper_name: shipper.name,
        shipper_name_uri: encodeURIComponent(shipper.name),
        carrier_guid: carrierSettings?.carrier.guid,
        carrier_name: carrierSettings?.carrier.name,
      };
      trackEvent('Carrier Reported Shipper', details);
      logInfo('Carrier Reported Shipper', {
        namespace: 'trust.reporting.carrier-reported-shipper',
        ...details,
      });
      addReportedShipperGUID(shipper.guid);
      onSubmitSuccess();
      formik.resetForm();
    },
    onSubmitFailure(error) {
      addSnackbar(error.message, { variant: 'error' });
    },
  });

  return (
    <FormikProvider value={formik}>
      <ShipperReportContent>
        <Stack space="small">
          <Stack>
            <Typography variant="h4">How should we contact you?</Typography>
            <FormikTextField
              name="contact"
              label="Preferred contact method"
              helperText="Enter your preferred email address or phone number "
              fullWidth={true}
            />
          </Stack>

          <FormikTextField
            name="description"
            multiline={true}
            fullWidth={true}
            label={
              <Typography variant="h4">
                Please provide a detailed description of the incident that
                prompted you to report fraud.
              </Typography>
            }
          />
        </Stack>
      </ShipperReportContent>
      <ShipperReportActions>
        <Stack>
          <Button
            pending={formik.isSubmitting}
            onClick={() => {
              formik.handleSubmit();
            }}
          >
            Report Fraud
          </Button>
          {!isEmpty(formik.errors) && (
            <TextBox color="red">
              Fill out highlighted fields to proceed.
            </TextBox>
          )}
        </Stack>
      </ShipperReportActions>
    </FormikProvider>
  );
}

export const REPORT_REASONS_TITLE: Record<ExtendedReason, string> = {
  DOUBLE_BROKERING: 'Report Double-Brokering',
  IDENTITY_THEFT: 'Report Identity Theft',
  NON_PAYMENT: 'Report Non-payment',
  UNAUTHORIZED_DISPATCHING: 'Report Unauthorized Dispatching',
  SUSPICIOUS_ACTIVITY: 'Report Suspicious Activity',
  OTHER: 'Report Other Activities',
  RATING: 'Leave a rating',
} as const;

interface ShipperReportingDrawerProps {
  shipper?: ShipperProfileDTO;
  isOpen: boolean;
  onClose: () => void;
}

export function ShipperReportingDrawer({
  shipper,
  isOpen,
  onClose,
}: ShipperReportingDrawerProps) {
  const [selectedReason, setSelectedReason] = useState<ExtendedReason>();
  const [currentScreen, setCurrentScreen] = useState<
    'reason-selector' | 'report-form' | 'rating-form' | 'confirmation'
  >('reason-selector');

  function cleanDrawerState() {
    setCurrentScreen('reason-selector');
    setSelectedReason(undefined);
  }

  function handleReasonSelect(reason: ExtendedReason) {
    setSelectedReason(reason);
    setCurrentScreen(
      isRatingReportReason(reason) ? 'rating-form' : 'report-form',
    );
    trackEvent(`Carrier Selected ${startCase(reason)} Reporting Reason`, {
      reason,
    });
  }

  function handleSubmitSuccess() {
    setCurrentScreen('confirmation');
  }

  function handleClose() {
    setCurrentScreen('reason-selector');
    onClose();
  }

  useEffect(() => {
    if (isOpen) {
      trackEvent('Carrier Opened Shipper Reporting Drawer');
    }
  }, [isOpen]);

  if (!shipper) {
    return null;
  }

  return (
    <AppDrawer
      onBack={selectedReason ? cleanDrawerState : undefined}
      size="lg"
      title={
        selectedReason ? REPORT_REASONS_TITLE[selectedReason] : 'Report Shipper'
      }
      open={isOpen}
      onClose={handleClose}
      disableContentPadding={true}
    >
      {currentScreen === 'reason-selector' && (
        <ShipperReportReasonSelector onReasonSelect={handleReasonSelect} />
      )}
      {currentScreen === 'report-form' && selectedReason && (
        <ShipperReportingForm
          shipper={shipper}
          reason={selectedReason}
          onSubmitSuccess={handleSubmitSuccess}
        />
      )}
      {currentScreen === 'rating-form' && selectedReason && (
        <ShipperRatingFormRestricted
          shipper={shipper}
          reason={selectedReason}
          onSubmitSuccess={handleSubmitSuccess}
        />
      )}
      {currentScreen === 'confirmation' && (
        <ShipperReportingConfirmation onFinish={handleClose} />
      )}
    </AppDrawer>
  );
}
